// src/api/clients.ts
import axiosClient from './axiosClient';

/** Каналы связи клиента, которые понимает бэкенд. */
export type ClientChannel = 'phone' | 'whatsapp' | 'telegram' | 'vk' | 'avito' | 'orion';

export interface ClientCallsSummary {
    total: number;
    missed: number;
    lastCallAt: string | null;
}

export interface ClientChatsSummary {
    conversationCount: number;
    messageCount: number;
    lastMessageAt: string | null;
    channels: string[];
}

export interface ClientLessonsSummary {
    total: number;
    lastLessonAt: string | null;
    nextLessonAt: string | null;
}

/** Проданный абонемент. Сумма — в копейках, как её отдаёт бэкенд. */
export interface ClientSale {
    id: string;
    title: string | null;
    soldAt: string;
    remainingLessons: number;
    /** Всего занятий в абонементе — нужно для цены одного занятия. */
    totalLessons?: number;
    /** Сумма продажи в копейках. */
    totalAmount: number;
    currency: string;
}

export interface ClientOrganizationRef {
    id: string;
    name: string;
}

/**
 * Карточка клиента из справочника организации (`GET /v1/clients/{id}`).
 *
 * Это «полная» карточка: помимо контактов она отдаёт проданные абонементы,
 * которых нет в карточке по номеру телефона.
 */
export interface IClientCard {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
    company: string | null;
    note: string | null;
    /** Появился вместе с полем в справочнике, поэтому необязателен. */
    channel?: string | null;
    createdAt: string;
    updatedAt: string;
    organization?: ClientOrganizationRef | null;
    calls: ClientCallsSummary;
    chats: ClientChatsSummary;
    lessons: ClientLessonsSummary;
    sales: ClientSale[];
}

export interface IClientFormPayload {
    name: string;
    phone?: string | null;
    email?: string | null;
    company?: string | null;
    note?: string | null;
    channel?: string | null;
}

interface ClientEnvelope {
    data: IClientCard;
}

/**
 * Полная карточка клиента из справочника.
 *
 * mode — организация консоли: клиенты одной компании не видны в другой,
 * без mode бэкенд отвечает ошибкой.
 */
export const fetchClient = async (id: string, mode: string): Promise<IClientCard> => {
    const response = await axiosClient.get<ClientEnvelope>(`/v1/clients/${id}`, {
        params: { mode },
    });

    return response.data.data;
};

/** Новый клиент организации. */
export const createClient = async (mode: string, payload: IClientFormPayload): Promise<IClientCard> => {
    const response = await axiosClient.post<ClientEnvelope>('/v1/clients', payload, {
        params: { mode },
    });

    return response.data.data;
};

/** Частичная правка клиента: меняем только переданные поля. */
export const updateClient = async (
    id: string,
    mode: string,
    payload: IClientFormPayload,
): Promise<IClientCard> => {
    const response = await axiosClient.patch<ClientEnvelope>(`/v1/clients/${id}`, payload, {
        params: { mode },
    });

    return response.data.data;
};

/**
 * Клиент в списке справочника (`GET /v1/clients`).
 *
 * Бэкенд отдаёт те же поля, что и в карточке, но без продаж: список нужен
 * для выбора, а не для работы с абонементами.
 */
export interface IClientListItem {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
    company: string | null;
    note: string | null;
    channel?: string | null;
    createdAt: string;
    updatedAt: string;
    calls: ClientCallsSummary;
}

/** Одна страница справочника вместе со счётчиками для подписей. */
export interface IClientsPage {
    items: IClientListItem[];
    page: number;
    perPage: number;
    total: number;
    summary: {
        total: number;
        withPhone: number;
        createdThisMonth: number;
    };
}

export interface SearchClientsParams {
    /** Организация консоли: без неё бэкенд список не отдаёт. */
    mode: string;
    /** Подстрока имени, телефона или компании. */
    query: string;
    /** Сколько клиентов взять за раз; бэкенд ограничивает сотней. */
    perPage?: number;
}

/**
 * Поиск клиентов организации по имени, телефону и компании.
 *
 * Фильтрацию делает бэкенд: на клиенте список не режем, иначе поиск
 * находил бы только уже загруженную страницу справочника.
 */
export const searchClients = async ({
    mode,
    query,
    perPage = 20,
}: SearchClientsParams): Promise<IClientsPage> => {
    const response = await axiosClient.get<IClientsPage>('/v1/clients', {
        params: { mode, search: query.trim(), perPage },
    });

    return response.data;
};
